import type { CompanySummary, ObraDetail, ReverseGeocode, SecretariaSummary } from '@obras/shared';
import { maskCurrency, OBRA_STATUS_LABELS, OBRA_STATUSES } from '@obras/shared';
import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  Check,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Image as ImageIcon,
  Landmark,
  MapPin,
  Plus,
} from 'lucide-react';
import { lazy, Suspense, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '../../components/ui/Button.js';
import { Checkbox, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { FormError } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { SingleSelect } from '../../components/ui/MultiSelect.js';
import { Tab, TabList } from '../../components/ui/Tabs.js';
import { api, ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { CompanyFormModal } from '../empresas/CompanyFormModal.js';
import { SecretariaFormModal } from '../secretarias/SecretariaFormModal.js';
import { CapaDaObra } from './CapaDaObra.js';
import { useUpdateObra } from './api.js';
import {
  coordOrNull,
  corpoDaObra,
  formularioInicial,
  useOpcoesDaObra,
  type FormularioDaObra,
} from './formularioDaObra.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * O mapa entra por carregamento tardio.
 *
 * Leaflet e a folha de estilo dele somam uns quarenta e cinco kilobytes que só
 * fazem falta dentro deste formulário, na última etapa. Embutidos no pacote
 * principal, seriam baixados por toda pessoa que abre o sistema para olhar a
 * lista de obras — em rede de cidade pequena isso é meio segundo de espera para
 * quem nunca vai cadastrar nada.
 */
const MapaLocal = lazy(async () => ({
  default: (await import('../../components/ui/MapaLocal.js')).MapaLocal,
}));

// ──────────────────────────────────────────────────────────── etapas

type EtapaId = 'identificacao' | 'capa' | 'empresa' | 'contrato' | 'fiscalizacao' | 'localizacao';

interface Etapa {
  readonly id: EtapaId;
  readonly rotulo: string;
  readonly icone: LucideIcon;
  readonly resumo: string;
  /** Campos do servidor que pertencem a esta etapa, para marcar o erro nela. */
  readonly campos: readonly string[];
}

const ETAPAS: readonly Etapa[] = [
  {
    id: 'identificacao',
    rotulo: 'Identificação',
    icone: ClipboardList,
    resumo: 'Denominação, objeto contratual, demandante e situação da execução.',
    campos: ['name', 'description', 'status', 'isReform', 'secretariaId'],
  },
  {
    id: 'capa',
    rotulo: 'Capa',
    icone: ImageIcon,
    resumo: 'Imagem que representa a obra nas listagens e na ficha.',
    campos: ['coverMediaId'],
  },
  {
    id: 'empresa',
    rotulo: 'Empresa',
    icone: Building2,
    resumo: 'Pessoa jurídica responsável pela execução do objeto.',
    campos: ['companyId'],
  },
  {
    id: 'contrato',
    rotulo: 'Contrato',
    icone: FileText,
    resumo: 'Identificação do instrumento, valor pactuado e vigências.',
    campos: [
      'contractNumber',
      'initialValueCents',
      'contractStart',
      'contractEnd',
      'osStart',
      'osEnd',
    ],
  },
  {
    id: 'fiscalizacao',
    rotulo: 'Fiscalização',
    icone: ClipboardCheck,
    resumo: 'Servidor designado e periodicidade das vistorias técnicas.',
    campos: ['visitIntervalDays', 'responsibleFiscalId'],
  },
  {
    id: 'localizacao',
    rotulo: 'Localização',
    icone: MapPin,
    resumo: 'Coordenadas geográficas e endereço do local da obra.',
    campos: ['latitude', 'longitude', 'address', 'city', 'state'],
  },
];

/**
 * Edição de obra, em etapas de livre navegação.
 *
 * O cadastro é a página `/obras/nova`, um assistente com revisão no fim. A
 * edição continua aqui, num modal com sumário: quem abre "Editar" vai corrigir
 * um campo, e não refazer o cadastro — então **nenhuma etapa tranca a
 * seguinte**, e a trilha à esquerda leva direto a qualquer uma. Quando o
 * servidor recusa, a etapa com campo errado ganha um ponto vermelho e o
 * formulário salta para ela.
 */
export function ObraFormModal({
  open,
  onClose,
  obra,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  obra: ObraDetail;
  onSaved?: (obra: ObraDetail) => void;
}) {
  const { can, may } = useAuth();

  /*
   * Só a gestão troca o responsável: transferir a obra é atribuir trabalho a
   * um colega, e o servidor recusaria de qualquer forma.
   */
  const podeDesignar = can('gestor');
  const notaDoResponsavel = obra.responsibleFiscal
    ? 'A transferência de titularidade compete à gestão.'
    : 'A designação do fiscal responsável compete à gestão.';

  /*
   * Cadastrar empresa e secretaria são permissões próprias. Quem não alcança
   * não recebe o botão: o servidor recusaria, e o formulário de obra não é o
   * lugar de descobrir isso.
   */
  const podeCadastrarEmpresa = may('canManageCompanies');
  const podeCadastrarSecretaria = may('canAccessSecretarias');

  const [form, setForm] = useState<FormularioDaObra>(() => formularioInicial(obra));
  const [etapa, setEtapa] = useState<EtapaId>('identificacao');

  /*
   * A capa vive em dois lugares: o `coverMediaId` vai no corpo da requisição, e
   * a URL serve só para a miniatura na tela. Guardá-las juntas evita uma ida ao
   * servidor só para saber que imagem o id representa.
   *
   * Na edição, `coverUrl` só é aproveitado quando a obra tem capa **própria**:
   * sem `coverMediaId` a URL é a de uma foto de relatório, herdada para o cartão
   * não ficar vazio, e exibi-la aqui faria o formulário prometer uma capa que
   * não existe.
   */
  const [capa, setCapa] = useState<{ mediaId: string; url: string } | null>(() =>
    obra.coverMediaId && obra.coverUrl ? { mediaId: obra.coverMediaId, url: obra.coverUrl } : null,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [novaEmpresa, setNovaEmpresa] = useState(false);
  const [novaSecretaria, setNovaSecretaria] = useState(false);
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);

  const update = useUpdateObra(obra.id);
  const opcoes = useOpcoesDaObra();
  const saving = update.isPending;

  const set = <K extends keyof FormularioDaObra>(key: K, value: FormularioDaObra[K]): void =>
    setForm((current) => ({ ...current, [key]: value }));

  /** Quais etapas têm campo recusado pelo servidor. */
  const etapasComErro = useMemo(() => {
    const comErro = new Set<EtapaId>();
    for (const passo of ETAPAS) {
      if (passo.campos.some((campo) => fieldErrors[campo])) comErro.add(passo.id);
    }
    return comErro;
  }, [fieldErrors]);

  /**
   * Traz logradouro, município e UF do ponto marcado.
   *
   * **Só preenche o que está em branco.** O endereço que veio do projeto é mais
   * preciso que o do OpenStreetMap, e quem já o digitou não pode perdê-lo por
   * ter ajustado o pino depois.
   *
   * Falha em silêncio: é uma comodidade, e os três campos continuam ali para
   * serem preenchidos à mão. Um alerta vermelho porque o Nominatim não respondeu
   * assustaria sem motivo.
   */
  async function preencherEndereco(lat: number, lng: number): Promise<void> {
    setBuscandoEndereco(true);

    try {
      const achado = await api.get<ReverseGeocode>(
        `/obras/endereco-por-coordenada?lat=${lat}&lng=${lng}`,
      );

      setForm((atualForm) => ({
        ...atualForm,
        address: atualForm.address || (achado.address ?? ''),
        city: atualForm.city || (achado.city ?? ''),
        state: atualForm.state || (achado.state ?? ''),
      }));
    } catch {
      // Silêncio proposital; ver o comentário acima.
    } finally {
      setBuscandoEndereco(false);
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    try {
      const saved = await update.mutateAsync(corpoDaObra(form));
      onSaved?.(saved);
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        const campos = error.fieldErrors();
        setFieldErrors(campos);
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);

        /*
         * Salta para a primeira etapa com erro. Sem isso, a mensagem falaria de
         * um campo que não está na tela — e o formulário pareceria travado.
         */
        const primeira = ETAPAS.find((passo) => passo.campos.some((campo) => campos[campo]));
        if (primeira) setEtapa(primeira.id);
      } else {
        setFormError('Não foi possível salvar. Tente novamente.');
      }
    }
  }

  const atual = ETAPAS.find((passo) => passo.id === etapa) ?? ETAPAS[0]!;
  const erro = (campo: string) => (fieldErrors[campo] ? { error: fieldErrors[campo] } : {});

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="xl"
        flush
        title="Editar obra"
        description="Dados do contrato, prazos, responsáveis e localização."
        footer={
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              form="obra-form"
              type="submit"
              loading={saving}
              icon={<Check {...ICON} size={16} />}
            >
              Salvar alterações
            </Button>
          </>
        }
      >
        {/*
         * Altura **fixa** a partir de `sm`, com a rolagem na coluna do
         * formulário.
         *
         * Mínima não bastava: com um piso de 500px o modal ainda ia a 795px na
         * Identificação e a 808px na Localização, porque o conteúdo passa do
         * piso. O rodapé subia e descia debaixo do ponteiro a cada troca de
         * etapa. Fixando a altura, quem rola é a coluna da direita e o modal
         * não se mexe.
         *
         * O `max-h-[85dvh]` do modal continua mandando: em tela baixa o corpo
         * rola por fora e a altura para de crescer — constante do mesmo jeito.
         *
         * No celular a altura fica livre. O modal já é uma folha que sobe do pé
         * da tela, e prender 520px numa etapa de dois campos deixaria metade da
         * folha vazia com uma barra de rolagem inútil ao lado.
         */}
        <div className="grid gap-6 px-6 pb-2 sm:h-[520px] sm:grid-cols-[196px_1fr]">
          {/*
           * A trilha rola junto em tela estreita, e vira coluna fixa a partir
           * de `sm`. Empilhada no celular ela fica no topo, que é onde um
           * sumário é procurado.
           */}
          <nav className="sm:sticky sm:top-0 sm:self-start">
            <TabList label="Etapas do cadastro" orientation="vertical">
              {ETAPAS.map((passo) => (
                <Tab
                  key={passo.id}
                  vertical
                  active={etapa === passo.id}
                  // O ponto marca a etapa com campo que o servidor recusou.
                  alert={etapasComErro.has(passo.id)}
                  icon={passo.icone}
                  onClick={() => setEtapa(passo.id)}
                >
                  {passo.rotulo}
                </Tab>
              ))}
            </TabList>
          </nav>

          <form
            id="obra-form"
            onSubmit={(event) => void handleSubmit(event)}
            /* `pr-1` afasta o conteúdo da barra de rolagem própria da coluna. */
            className="flex min-w-0 flex-col gap-5 sm:overflow-y-auto sm:pr-1"
            noValidate
          >
            <header>
              <h3 className="text-[15px] font-semibold tracking-tight">{atual.rotulo}</h3>
              <p className="mt-0.5 text-[13px] text-ink-3">{atual.resumo}</p>
            </header>

            {formError ? <FormError>{formError}</FormError> : null}

            {etapa === 'identificacao' ? (
              <Painel>
                <TextField
                  label="Denominação da obra"
                  required
                  autoFocus
                  placeholder="Construção da Unidade Básica de Saúde do Bairro Jardim"
                  hint="Como a obra será identificada em relatórios e listagens."
                  value={form.name}
                  onChange={(event) => set('name', event.target.value)}
                  {...erro('name')}
                />

                <TextAreaField
                  label="Objeto"
                  rows={4}
                  hint="Transcreva o objeto conforme redigido no instrumento contratual."
                  value={form.description}
                  onChange={(event) => set('description', event.target.value)}
                  {...erro('description')}
                />

                <SelectField
                  label="Situação da execução"
                  value={form.status}
                  onChange={(event) => set('status', event.target.value)}
                  {...erro('status')}
                >
                  {OBRA_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {OBRA_STATUS_LABELS[status]}
                    </option>
                  ))}
                </SelectField>

                <Checkbox
                  label="Reforma ou ampliação de edificação existente"
                  hint="Distingue a intervenção em edificação existente da construção nova nos relatórios gerenciais."
                  checked={form.isReform}
                  onChange={(event) => set('isReform', event.target.checked)}
                />

                {/*
                 * A secretaria demandante fica ao lado da situação: as duas
                 * respondem "de quem é esta obra e como ela está", e é assim que
                 * a ficha da obra as apresenta depois.
                 */}
                <div>
                  <SingleSelect
                    label="Secretaria demandante"
                    searchable
                    placeholder={
                      opcoes.carregando.secretarias ? 'Carregando…' : 'Buscar por nome ou sigla'
                    }
                    options={opcoes.secretarias}
                    value={form.secretariaId || null}
                    onChange={(value) => set('secretariaId', value)}
                  />
                  <p className="mt-1.5 text-[12px] text-ink-3">
                    Órgão interno que solicitou a obra e responde por ela.
                  </p>
                  {fieldErrors['secretariaId'] ? (
                    <p className="mt-1.5 text-[12px] text-danger">{fieldErrors['secretariaId']}</p>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  {podeCadastrarSecretaria ? (
                    <Button
                      size="sm"
                      icon={<Landmark {...ICON} size={14} />}
                      onClick={() => setNovaSecretaria(true)}
                    >
                      Cadastrar secretaria
                    </Button>
                  ) : null}
                  {form.secretariaId ? (
                    <Button size="sm" variant="ghost" onClick={() => set('secretariaId', '')}>
                      Limpar seleção
                    </Button>
                  ) : null}
                </div>
              </Painel>
            ) : null}

            {etapa === 'capa' ? (
              <Painel>
                <CapaDaObra
                  obraId={obra.id}
                  atual={capa}
                  onChange={(escolhida) => {
                    setCapa(escolhida);
                    set('coverMediaId', escolhida?.mediaId ?? '');
                  }}
                />
              </Painel>
            ) : null}

            {etapa === 'empresa' ? (
              <Painel>
                <div>
                  <SingleSelect
                    label="Empresa contratada"
                    searchable
                    placeholder={
                      opcoes.carregando.empresas
                        ? 'Carregando empresas…'
                        : 'Buscar por nome ou CNPJ'
                    }
                    options={opcoes.empresas}
                    value={form.companyId || null}
                    onChange={(value) => set('companyId', value)}
                  />
                  {fieldErrors['companyId'] ? (
                    <p className="mt-1.5 text-[12px] text-danger">{fieldErrors['companyId']}</p>
                  ) : null}
                </div>

                {/*
                 * Cadastrar a empresa daqui, e não em outra tela: a licitação
                 * termina e a obra é cadastrada no mesmo dia, com a empresa
                 * ainda fora do sistema. Sair para cadastrá-la perderia o que já
                 * foi preenchido.
                 */}
                <div className="bg-sunken px-4 py-3.5">
                  <p className="text-[13px] font-medium">Empresa ainda não cadastrada</p>
                  {podeCadastrarEmpresa ? (
                    <>
                      <p className="mt-0.5 text-[13px] text-ink-3">
                        Efetue o cadastro sem sair deste formulário. A partir do CNPJ, o sistema
                        recupera razão social, endereço e telefone na base da Receita Federal.
                      </p>
                      <Button
                        size="sm"
                        variant="surface"
                        className="mt-3"
                        icon={<Plus {...ICON} size={14} />}
                        onClick={() => setNovaEmpresa(true)}
                      >
                        Cadastrar nova empresa
                      </Button>
                    </>
                  ) : (
                    <p className="mt-0.5 text-[13px] text-ink-3">
                      O cadastro da empresa contratada depende do acesso "Gerenciar empresas". A
                      obra pode ser registrada agora e vinculada à empresa depois.
                    </p>
                  )}
                </div>

                {form.companyId ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="self-start"
                    onClick={() => set('companyId', '')}
                  >
                    Limpar seleção
                  </Button>
                ) : null}
              </Painel>
            ) : null}

            {etapa === 'contrato' ? (
              <Painel>
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    label="Número do contrato"
                    mono
                    placeholder="056/2026"
                    value={form.contractNumber}
                    onChange={(event) => set('contractNumber', event.target.value)}
                    {...erro('contractNumber')}
                  />

                  {/*
                   * A máscara entra a cada tecla, da direita para a esquerda.
                   * Formatar só ao sair do campo deixava o número ambíguo
                   * justamente enquanto a pessoa o conferia.
                   */}
                  <TextField
                    label="Valor inicial contratado"
                    mono
                    inputMode="numeric"
                    placeholder="R$ 0,00"
                    hint="Aditivos e supressões são registrados por relatório."
                    value={form.initialValue}
                    onChange={(event) => set('initialValue', maskCurrency(event.target.value))}
                    {...erro('initialValueCents')}
                  />
                </div>

                <div>
                  <h4 className="mb-3 text-[11px] font-medium tracking-[0.04em] text-ink-3 uppercase">
                    Vigência do contrato
                  </h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="Início"
                      type="date"
                      mono
                      value={form.contractStart}
                      onChange={(event) => set('contractStart', event.target.value)}
                      {...erro('contractStart')}
                    />
                    <TextField
                      label="Encerramento"
                      type="date"
                      mono
                      value={form.contractEnd}
                      onChange={(event) => set('contractEnd', event.target.value)}
                      {...erro('contractEnd')}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="mb-3 text-[11px] font-medium tracking-[0.04em] text-ink-3 uppercase">
                    Ordem de serviço
                  </h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="Emissão"
                      type="date"
                      mono
                      hint="Marco inicial da contagem do prazo de execução."
                      value={form.osStart}
                      onChange={(event) => set('osStart', event.target.value)}
                      {...erro('osStart')}
                    />
                    <TextField
                      label="Término previsto"
                      type="date"
                      mono
                      value={form.osEnd}
                      onChange={(event) => set('osEnd', event.target.value)}
                      {...erro('osEnd')}
                    />
                  </div>
                </div>
              </Painel>
            ) : null}

            {etapa === 'fiscalizacao' ? (
              <Painel>
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    label="Periodicidade das vistorias"
                    type="number"
                    min={1}
                    max={365}
                    mono
                    placeholder="15"
                    hint="Intervalo em dias. Não informado, aplica-se o padrão do órgão."
                    value={form.visitIntervalDays}
                    onChange={(event) => set('visitIntervalDays', event.target.value)}
                    {...erro('visitIntervalDays')}
                  />

                  {podeDesignar ? (
                    <div>
                      <SingleSelect
                        label="Fiscal responsável"
                        searchable
                        placeholder={
                          opcoes.carregando.fiscais ? 'Carregando…' : 'Nenhum servidor designado'
                        }
                        options={opcoes.fiscais}
                        value={form.responsibleFiscalId || null}
                        onChange={(value) => set('responsibleFiscalId', value)}
                      />
                      {fieldErrors['responsibleFiscalId'] ? (
                        <p className="mt-1.5 text-[12px] text-danger">
                          {fieldErrors['responsibleFiscalId']}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      <span className="mb-1.5 block text-[13px] font-medium">
                        Fiscal responsável
                      </span>
                      <p className="flex h-10 items-center bg-sunken px-3 text-[13px]">
                        {obra.responsibleFiscal?.name ?? 'Nenhum fiscal designado'}
                      </p>
                      <p className="mt-1.5 text-[12px] text-ink-3">{notaDoResponsavel}</p>
                    </div>
                  )}
                </div>
              </Painel>
            ) : null}

            {etapa === 'localizacao' ? (
              <Painel>
                <div>
                  <Suspense
                    fallback={
                      <div className="grid h-80 w-full place-items-center bg-sunken text-[13px] text-ink-3">
                        Carregando o mapa…
                      </div>
                    }
                  >
                    <MapaLocal
                      latitude={coordOrNull(form.latitude)}
                      longitude={coordOrNull(form.longitude)}
                      onChange={(lat, lng) => {
                        setForm((atualForm) => ({
                          ...atualForm,
                          latitude: String(lat),
                          longitude: String(lng),
                        }));
                        void preencherEndereco(lat, lng);
                      }}
                    />
                  </Suspense>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="Latitude"
                      mono
                      inputMode="decimal"
                      placeholder="-10.178211"
                      hint="Preenchida pela marcação no mapa ou informada diretamente."
                      value={form.latitude}
                      onChange={(event) => set('latitude', event.target.value)}
                      {...erro('latitude')}
                    />
                    <TextField
                      label="Longitude"
                      mono
                      inputMode="decimal"
                      placeholder="-59.445292"
                      value={form.longitude}
                      onChange={(event) => set('longitude', event.target.value)}
                      {...erro('longitude')}
                    />
                  </div>

                  <div className="mt-4 grid gap-4">
                    <TextField
                      label="Logradouro"
                      placeholder="Avenida Ari Mendes da Silva, s/nº"
                      hint={
                        buscandoEndereco
                          ? 'Consultando o endereço do ponto marcado…'
                          : 'Preenchido a partir do mapa quando o campo está vazio.'
                      }
                      value={form.address}
                      onChange={(event) => set('address', event.target.value)}
                      {...erro('address')}
                    />

                    <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
                      <TextField
                        label="Município"
                        value={form.city}
                        onChange={(event) => set('city', event.target.value)}
                        {...erro('city')}
                      />
                      <TextField
                        label="UF"
                        maxLength={2}
                        mono
                        placeholder="MT"
                        value={form.state}
                        onChange={(event) => set('state', event.target.value.toUpperCase())}
                        {...erro('state')}
                      />
                    </div>
                  </div>
                </div>
              </Painel>
            ) : null}
          </form>
        </div>
      </Modal>

      {/*
       * Um `<dialog>` dentro de outro: os dois vão para a camada superior do
       * navegador, e o de cima recebe o foco. Fechá-lo devolve o foco ao
       * formulário da obra, com a empresa recém-criada já selecionada.
       */}
      <CompanyFormModal
        open={novaEmpresa}
        onClose={() => setNovaEmpresa(false)}
        onSaved={(empresa: CompanySummary) => set('companyId', empresa.id)}
      />

      <SecretariaFormModal
        open={novaSecretaria}
        onClose={() => setNovaSecretaria(false)}
        onSaved={(criada: SecretariaSummary) => set('secretariaId', criada.id)}
      />
    </>
  );
}

/** O corpo de uma etapa. Espaçamento único, para as quatro não divergirem. */
function Painel({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>;
}
